import { Shield, ShieldCheck, ShieldQuestion } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type VerificationTier = "verified" | "partner" | "community";

interface FlexauraVerifiedBadgeProps {
  tier: VerificationTier;
  verifiedDate?: string; // ISO date
  verifiedProvider?: string;
  className?: string;
}

const TIER_CONFIG: Record<
  VerificationTier,
  {
    label: string;
    tooltip: (provider?: string, dateLabel?: string) => string;
    bgClass: string;
    borderColor: string;
    textClass: string;
    Icon: typeof Shield;
  }
> = {
  verified: {
    label: "Flexaura Verified",
    tooltip: (provider, dateLabel) =>
      `This provider has been confirmed active on ${provider || "provider catalogues"} as of ${dateLabel || "this month"}`,
    bgClass: "bg-[hsl(40_55%_51%/0.1)]",
    borderColor: "border-[hsl(40_55%_51%)]",
    textClass: "text-[hsl(40_55%_51%)]",
    Icon: ShieldCheck,
  },
  partner: {
    label: "Official Partner",
    tooltip: () =>
      "Flexaura has a formal partnership with this provider. Transactions are tracked and protected.",
    bgClass: "bg-[hsl(210_53%_23%/0.1)]",
    borderColor: "border-[hsl(210_53%_23%)]",
    textClass: "text-[hsl(210_53%_23%)]",
    Icon: Shield,
  },
  community: {
    label: "Community Listed",
    tooltip: () =>
      "Listed based on provider catalogues. Verify availability at checkout.",
    bgClass: "bg-muted",
    borderColor: "border-muted-foreground/30",
    textClass: "text-muted-foreground",
    Icon: ShieldQuestion,
  },
};

function formatMonth(iso?: string): string {
  if (!iso) return new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

const FlexauraVerifiedBadge = ({
  tier,
  verifiedDate,
  verifiedProvider,
  className = "",
}: FlexauraVerifiedBadgeProps) => {
  const config = TIER_CONFIG[tier];
  const dateLabel = formatMonth(verifiedDate);
  const tooltipText = config.tooltip(verifiedProvider, dateLabel);
  const Icon = config.Icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium leading-tight ${config.bgClass} ${config.borderColor} ${config.textClass} ${className}`}
          >
            <Icon className="w-3 h-3 flex-shrink-0" />
            <span>{config.label}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[220px] text-xs">
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default FlexauraVerifiedBadge;

/** Small data-freshness line beneath a badge */
export const VerificationDisclaimer = ({
  verifiedProvider,
  verifiedDate,
}: {
  verifiedProvider?: string;
  verifiedDate?: string;
}) => {
  const dateLabel = formatMonth(verifiedDate);
  return (
    <p
      className="text-[10px] leading-tight text-muted-foreground font-mono"
    >
      Verified via {verifiedProvider || "catalogue"} · {dateLabel} · Confirm at checkout
    </p>
  );
};
